import {
  indexSegments,
  PART_IMAGE_SEGMENTATION_OPTIONS,
  PartImageSegments,
} from '@physna/core/domain/part-image-identification/part-image-segments';
import { mapWithConcurrency } from '@physna/core/domain/common/map-with-concurrency';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { useQuery } from '@tanstack/react-query';
import { isAxiosError } from 'axios';

const SEGMENTED_MAX_SIDE = 1024;
const SEGMENTED_JPEG_QUALITY = 0.85;
const BUSY_RETRY_DELAYS_MS = [1000, 2000, 4000];
const SERVICE_BUSY = 503;
const ONE_AT_A_TIME = 1;

export type PartImageSegmentsByPhoto = Map<number, PartImageSegments>;

async function downscaledJpeg(photo: File): Promise<File> {
  const bitmap = await createImageBitmap(photo, { imageOrientation: 'from-image' });
  const scale = Math.min(1, SEGMENTED_MAX_SIDE / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale);
  canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d')?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', SEGMENTED_JPEG_QUALITY));
  if (!blob) throw new Error('Could not prepare the photo for segmentation');
  return new File([blob], 'photo.jpg', { type: 'image/jpeg' });
}

const wait = (milliseconds: number) => new Promise(resolve => setTimeout(resolve, milliseconds));

const isBusy = (error: unknown): boolean => isAxiosError(error) && error.response?.status === SERVICE_BUSY;

async function segmentWhenFree(
  tenantId: string,
  file: File,
  retryDelays = BUSY_RETRY_DELAYS_MS
): Promise<PartImageSegments> {
  try {
    const response = await api.assets.segmentImage(tenantId, {
      file,
      pointsPerSide: String(PART_IMAGE_SEGMENTATION_OPTIONS.pointsPerSide),
      sensitivity: String(PART_IMAGE_SEGMENTATION_OPTIONS.sensitivity),
    });
    return indexSegments(response.data);
  } catch (error) {
    if (retryDelays.length === 0 || !isBusy(error)) throw error;
    const [delay, ...laterDelays] = retryDelays;
    await wait(delay);
    return segmentWhenFree(tenantId, file, laterDelays);
  }
}

async function segmentPhotoBestEffort(tenantId: string, photo: File): Promise<PartImageSegments | undefined> {
  try {
    return await segmentWhenFree(tenantId, await downscaledJpeg(photo));
  } catch {
    return undefined;
  }
}

async function segmentPhotos(tenantId: string, photos: File[]): Promise<PartImageSegmentsByPhoto> {
  const segmented = await mapWithConcurrency(photos, ONE_AT_A_TIME, photo => segmentPhotoBestEffort(tenantId, photo));
  return new Map(
    segmented.flatMap((segments, index): [number, PartImageSegments][] => (segments ? [[index + 1, segments]] : []))
  );
}

export const usePartImageSegments = (photos: File[], enabled: boolean) => {
  const { getTenantId } = useTenants();
  const { isImageSearchCroppingDisabled } = useFeatureFlags();
  const tenantId = getTenantId();
  const { data, isFetching } = useQuery({
    queryKey: ['partImageSegments', tenantId, photos.map(photo => [photo.name, photo.size, photo.lastModified])],
    queryFn: () => segmentPhotos(tenantId, photos),
    enabled: enabled && !isImageSearchCroppingDisabled && photos.length > 0 && !!tenantId,
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  });
  return { segments: data, isSegmenting: isFetching, canSegment: !isImageSearchCroppingDisabled };
};
